import React, { FC } from 'react';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import { WearableDataUtils } from '../../../common/utils/services/wearable-data-utils';
import { useTranslation } from 'react-i18next';

interface OvWearableScoreRingProps {
  score: number;
  label: string;
  compact?: boolean;
  variant?: 'light' | 'dark';
}

const clampScore = (score: number): number => Math.min(Math.max(score, 0), 100);

const OvWearableScoreRing: FC<OvWearableScoreRingProps> = ({
  score,
  label,
  compact = false,
  variant = 'light',
}) => {
  const { t } = useTranslation();
  const safeScore = clampScore(score);
  const interpretation = WearableDataUtils.getScoreInterpretation(safeScore);
  const scoreColor = WearableDataUtils.getScoreInterpretationColor(safeScore);
  const interpretationLabel = interpretation
    ? t(`common.wearable.score.${interpretation}`)
    : '';

  return (
    <Container>
      <Ring
        $score={safeScore}
        $scoreColor={scoreColor}
        $compact={compact}
        $variant={variant}
        role="img"
        aria-label={`${t('common.wearable.scoreOutOf100', {
          label,
          score: safeScore,
        })}${interpretationLabel ? `, ${interpretationLabel}` : ''}`}
      >
        <RingInner $compact={compact} $variant={variant}>
          {safeScore}
        </RingInner>
      </Ring>
      <TextGroup>
        <Label $variant={variant}>{label}</Label>
        {interpretationLabel ? (
          <Interpretation $variant={variant}>
            {interpretationLabel}
          </Interpretation>
        ) : null}
      </TextGroup>
    </Container>
  );
};

export default OvWearableScoreRing;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
`;

const Ring = styled.div<{
  $score: number;
  $scoreColor: string;
  $compact: boolean;
  $variant: 'light' | 'dark';
}>`
  width: ${(props) => (props.$compact ? '2.5rem' : '3rem')};
  height: ${(props) => (props.$compact ? '2.5rem' : '3rem')};
  border-radius: ${Variables.borderRadius.CIRCLE};
  background: ${(props) =>
    `conic-gradient(${props.$scoreColor} ${props.$score * 3.6}deg, ${
      props.$variant === 'dark' ? Colours.OV_GRAY : Colours.OV_DISABLED
    } 0deg)`};
  display: grid;
  place-items: center;
  flex: 0 0 ${(props) => (props.$compact ? '2.5rem' : '3rem')};
`;

const RingInner = styled.span<{
  $compact: boolean;
  $variant: 'light' | 'dark';
}>`
  width: ${(props) => (props.$compact ? '1.875rem' : '2.25rem')};
  height: ${(props) => (props.$compact ? '1.875rem' : '2.25rem')};
  border-radius: ${Variables.borderRadius.CIRCLE};
  background-color: ${(props) =>
    props.$variant === 'dark' ? Colours.OV_BASE : Colours.OV_SEMI_LIGHT};
  color: ${(props) =>
    props.$variant === 'dark' ? Colours.OV_SEMI_LIGHT : Colours.OV_BASE};
  display: grid;
  place-items: center;
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Label = styled.span<{ $variant: 'light' | 'dark' }>`
  color: ${(props) =>
    props.$variant === 'dark' ? Colours.OV_SEMI_LIGHT : Colours.OV_BASE};
  font-size: ${Variables.fontSizes.SMALL};
  font-weight: bold;
`;

const Interpretation = styled.span<{ $variant: 'light' | 'dark' }>`
  color: ${(props) =>
    props.$variant === 'dark' ? Colours.OV_GRAY : Colours.OV_CHARCOAL};
  font-size: ${Variables.fontSizes.SMALL};
`;
